import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Link2, Plus, X, Users, Search } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CustomerManageLinks = ({ customerId, customerName }) => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [query, setQuery] = useState('');
  const [allCustomers, setAllCustomers] = useState([]);

  const token = () => localStorage.getItem('token');

  const load = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/manage-links?customer_id=${customerId}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      setLinks(res.data || []);
    } catch (_) { /* ignore */ } finally { setLoading(false); }
  }, [customerId]);

  useEffect(() => { load(); }, [load]);

  const openAdd = async () => {
    setAdding(true);
    if (allCustomers.length === 0) {
      try {
        const res = await axios.get(`${API}/customers/with-users`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        setAllCustomers(res.data || []);
      } catch (_) { /* ignore */ }
    }
  };

  const createLink = async (peerId) => {
    try {
      await axios.post(`${API}/admin/manage-links`,
        { customer_a_id: customerId, customer_b_id: peerId },
        { headers: { Authorization: `Bearer ${token()}` } });
      toast.success('Termin-Verwaltung erlaubt');
      setAdding(false); setQuery('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Anlegen');
    }
  };

  const removeLink = async (id) => {
    try {
      await axios.delete(`${API}/admin/manage-links/${id}`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      toast.success('Beziehung entfernt');
      load();
    } catch (e) {
      toast.error('Fehler beim Entfernen');
    }
  };

  const peerName = (link) => {
    const other = (link.members || []).find(m => m.customer_id !== customerId);
    return other?.name || 'Unbekannt';
  };

  const filtered = allCustomers
    .filter(c => c.id !== customerId)
    .filter(c => {
      const n = `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase();
      return !query || n.includes(query.toLowerCase());
    })
    .slice(0, 8);

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-4" data-testid="manage-links-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-zinc-800">
          <Link2 className="h-4 w-4 text-teal-600" /> Termin-Verwaltung (darf für einander absagen/buchen)
        </div>
        {!adding && (
          <Button size="sm" variant="outline" onClick={openAdd} data-testid="manage-link-add-btn">
            <Plus className="h-4 w-4 mr-1" /> Erlauben
          </Button>
        )}
      </div>

      {adding && (
        <div className="mb-3 rounded-md bg-zinc-50 p-3">
          <div className="flex items-center gap-2 mb-2">
            <Search className="h-4 w-4 text-zinc-400" />
            <Input
              autoFocus
              placeholder="Kunde suchen…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="manage-link-search"
            />
            <Button size="sm" variant="ghost" onClick={() => { setAdding(false); setQuery(''); }}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="space-y-1 max-h-56 overflow-auto">
            {filtered.map(c => (
              <button
                key={c.id}
                onClick={() => createLink(c.id)}
                className="w-full text-left px-2 py-1.5 rounded hover:bg-teal-50 text-sm text-zinc-700"
                data-testid={`manage-link-option-${c.id}`}
              >
                {c.first_name} {c.last_name}
              </button>
            ))}
            {filtered.length === 0 && <div className="text-xs text-zinc-400 px-2 py-1">Keine Treffer</div>}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-xs text-zinc-400">Lädt…</div>
      ) : links.length === 0 ? (
        <div className="text-xs text-zinc-400 flex items-center gap-1">
          <Users className="h-3.5 w-3.5" /> Keine Beziehungen. {customerName || 'Dieser Kunde'} kann nur eigene Termine absagen.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {links.map(link => (
            <li key={link.id} className="flex items-center justify-between rounded bg-zinc-50 px-3 py-2 text-sm" data-testid={`manage-link-${link.id}`}>
              <span className="text-zinc-700">{peerName(link)}</span>
              <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-600 h-7"
                onClick={() => removeLink(link.id)} data-testid={`manage-link-remove-${link.id}`}>
                <X className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default CustomerManageLinks;
